"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ArrowRight, Play, Pause, Mic, Headphones } from "lucide-react";
import Link from "next/link";
import { useLang } from "@/context/lang";

const heroVoices: Record<string, { name: string; accent: string; voiceId: string; flag: string; desc: string; introText: string }> = {
  en: { name: "Mira", accent: "American", voiceId: "21m00Tcm4TlvDq8ikWAM", flag: "\u{1F1FA}\u{1F1F8}", desc: "Professional, natural intonation",
    introText: "Welcome to SpeakRoar, a complete AI voice studio. Generate professional voiceovers in twenty five plus languages. Built-in audio editor, video dubbing, and long form text support. Try it free today!" },
  tr: { name: "Defne", accent: "Turkish", voiceId: "dKmdJ8jq2hPAyWUBHu3C", flag: "\u{1F1F9}\u{1F1F7}", desc: "Profesyonel, dogal tonlama",
    introText: "SpeakRoar'a hosgeldiniz! Yirmi besten fazla dilde profesyonel seslendirme uretebilirsiniz. Dahili ses editoru, video dublaj araci ve uzun metin destegi. Hemen ucretsiz deneyin!" },
  es: { name: "Sofia", accent: "Spanish", voiceId: "21m00Tcm4TlvDq8ikWAM", flag: "\u{1F1EA}\u{1F1F8}", desc: "Profesional, entonacion natural",
    introText: "Bienvenido a SpeakRoar. Un estudio de voz con inteligencia artificial completo. Genera locuciones profesionales en veinticinco idiomas. Editor de audio integrado y soporte para textos largos. Pruebalo gratis hoy!" },
  bs: { name: "Oliver", accent: "British", voiceId: "onwK4e9ZLuTAKqWW03F9", flag: "\u{1F1EC}\u{1F1E7}", desc: "Professional, natural intonation",
    introText: "Welcome to SpeakRoar. A complete AI voice studio for professional voiceovers in twenty five plus languages. Built-in audio editor, video dubbing tools, and long form text support. Try it free!" },
  ru: { name: "Natasha", accent: "Russian", voiceId: "21m00Tcm4TlvDq8ikWAM", flag: "\u{1F1F7}\u{1F1FA}", desc: "Professional, natural intonation",
    introText: "Dobro pozhalovat v SpeakRoar! Polnotsennaya AI studiya golosa dlya professional'noy ozvuchki na dvadtsati pyati yazykakh. Poprobujte besplatno!" },
  de: { name: "Hans", accent: "German", voiceId: "pNInz6obpgDQGcFmaJgB", flag: "\u{1F1E9}\u{1F1EA}", desc: "Professionell, naturliche Intonation",
    introText: "Willkommen bei SpeakRoar! Ein vollstaendiges KI Sprachstudio fuer professionelle Sprachausgabe in mehr als fuenfundzwanzig Sprachen. Integrierter Audioeditor und Langtext Unterstuetzung. Testen Sie es kostenlos!" },
};

export function Hero() {
  const { T, lang } = useLang();
  const [loggedIn, setLoggedIn] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [heroPlaying, setHeroPlaying] = useState(false);
  const [heroLoading, setHeroLoading] = useState(false);
  const [studioLoading, setStudioLoading] = useState(false);
  const heroAudioRef = useRef<HTMLAudioElement | null>(null);
  // Alternating title (a → b → a → ...) every 5 seconds
  const [titleIdx, setTitleIdx] = useState(0);

  const openStudio = async () => {
    setStudioLoading(true);
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "https://studio.speakroar.com";
    try {
      const resp = await fetch("/api/auth/create-otp", { method: "POST" });
      const data = await resp.json();
      if (data.success && data.token) {
        window.open(`${apiUrl}/auto-login/${data.token}`, "_blank");
      } else {
        window.open(apiUrl, "_blank");
      }
    } catch {
      window.open(apiUrl, "_blank");
    } finally {
      setStudioLoading(false);
    }
  };

  useEffect(() => {
    setMounted(true);
    setLoggedIn(!!localStorage.getItem("speakroar-user"));
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTitleIdx((i) => (i + 1) % 2), 5000);
    return () => clearInterval(id);
  }, []);

  // Stop audio when lang changes
  useEffect(() => {
    heroAudioRef.current?.pause();
    setHeroPlaying(false);
  }, [lang]);

  const currentVoice = heroVoices[lang] || heroVoices.en;

  const playHeroVoice = () => {
    if (heroPlaying) {
      heroAudioRef.current?.pause();
      setHeroPlaying(false);
      return;
    }
    setHeroLoading(true);
    fetch("/api/public/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ voice_id: currentVoice.voiceId, accent: currentVoice.accent, text: currentVoice.introText }),
    })
      .then(r => { if (!r.ok) throw new Error("Failed"); return r.blob(); })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        heroAudioRef.current = audio;
        audio.onended = () => setHeroPlaying(false);
        audio.play()
          .then(() => { setHeroPlaying(true); setHeroLoading(false); })
          .catch(() => { setHeroPlaying(false); setHeroLoading(false); URL.revokeObjectURL(url); });
      })
      .catch(() => { setHeroLoading(false); setHeroPlaying(false); });
  };

  return (
    <section className="relative w-full pt-28 pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-cyan-50/40 to-white dark:from-slate-950 dark:via-slate-950 dark:to-slate-950 overflow-hidden">
      {/* Background decoration - animated blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-10 left-1/4 w-[500px] h-[500px] bg-cyan-200/20 dark:bg-cyan-500/10 rounded-full blur-3xl"
          animate={{
            x: [0, 30, -20, 0],
            y: [0, -25, 15, 0],
            scale: [1, 1.1, 0.95, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-10 right-1/4 w-[400px] h-[400px] bg-teal-200/20 dark:bg-purple-500/10 rounded-full blur-3xl"
          animate={{
            x: [0, -25, 20, 0],
            y: [0, 20, -15, 0],
            scale: [1, 0.95, 1.1, 1],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-100/50 dark:bg-cyan-500/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Content */}
          <div className="flex flex-col space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.05, ease: [0.23, 1, 0.32, 1] }}
              className="flex items-center gap-3 rounded-full border border-cyan-200 dark:border-cyan-400/30 bg-cyan-50 dark:bg-cyan-500/15 p-1 w-fit"
            >
              <motion.span
                className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-xs font-bold"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                NEW
              </motion.span>
              <span className="text-sm text-neutral-700 dark:text-white/90 mr-3 font-medium">
                {T("hero.badge")}
              </span>
            </motion.div>

            <h1 className="relative text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight font-bold text-neutral-900 dark:text-white leading-[1.08] min-h-[1.08em]">
              <AnimatePresence mode="wait">
                <motion.span
                  key={titleIdx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
                  className="block"
                >
                  {titleIdx === 0 ? (
                    <>
                      {T("hero.title1")}{" "}
                      <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">
                        {T("hero.title2")}
                      </span>{" "}
                      {T("hero.title3")}
                    </>
                  ) : (
                    <>
                      {T("hero.title1b")}{" "}
                      <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">
                        {T("hero.title2b")}
                      </span>
                    </>
                  )}
                </motion.span>
              </AnimatePresence>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.25, ease: [0.23, 1, 0.32, 1] }}
              className="text-lg sm:text-xl text-neutral-600 dark:text-slate-200 leading-relaxed max-w-xl"
            >
              {T("hero.desc")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.4, ease: [0.23, 1, 0.32, 1] }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                {mounted && loggedIn ? (
                  <button
                    onClick={openStudio}
                    disabled={studioLoading}
                    className="group px-8 py-4 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-semibold text-base hover:shadow-xl hover:shadow-cyan-500/25 transition-all duration-300 flex items-center gap-2 disabled:opacity-60"
                  >
                    {studioLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Mic size={18} />
                    )}
                    {T("nav.go_studio")}
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                ) : (
                  <Link
                    href="/register"
                    className="group px-8 py-4 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-semibold text-base hover:shadow-xl hover:shadow-cyan-500/25 transition-all duration-300 flex items-center gap-2"
                  >
                    {T("hero.cta1")}
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                )}
              </motion.div>
              <motion.a
                href="#nasil-calisir"
                className="px-8 py-4 rounded-full border border-neutral-300 text-neutral-700 dark:text-white/90 font-semibold text-base hover:border-cyan-300 dark:hover:border-cyan-400/50 hover:bg-cyan-50/50 transition-all duration-200 flex items-center gap-2"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
              >
                <Play size={16} className="fill-current" />
                {T("hero.cta2")}
              </motion.a>
            </motion.div>

          </div>

          {/* Right - Animated Waveform Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3, type: "spring", stiffness: 100 }}
            className="relative"
          >
            <div className="relative bg-white dark:bg-slate-900 rounded-3xl border border-neutral-200 dark:border-white/10 shadow-2xl shadow-cyan-500/10 p-8 space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg shadow-cyan-500/30 text-lg">
                    {currentVoice.flag}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-neutral-900 dark:text-white">{currentVoice.name} - {currentVoice.accent}</div>
                    <div className="text-xs text-neutral-500 dark:text-white/80">{currentVoice.desc}</div>
                  </div>
                </div>
                <div className="text-xs font-medium text-cyan-600 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-500/15 px-3 py-1.5 rounded-full border border-cyan-100 dark:border-cyan-400/30">
                  HD 44.1kHz
                </div>
              </div>

              {/* Animated waveform */}
              <div className="flex items-center gap-[2px] h-20 px-2">
                {Array.from({ length: 56 }).map((_, i) => {
                  const a = Math.sin(i * 0.4) * 22 + 24;
                  const b = Math.sin(i * 0.7 + 1.2) * 18 + 20;
                  const c = Math.sin(i * 0.3 + 2.5) * 26 + 28;
                  return (
                    <motion.div
                      key={i}
                      className="flex-1 min-w-[2px] bg-gradient-to-t from-cyan-500 to-teal-400 rounded-full opacity-90"
                      initial={{ height: 4 }}
                      animate={{ height: [4, a, b, c, 4] }}
                      transition={{
                        duration: 3 + (i % 5) * 0.3,
                        repeat: Infinity,
                        delay: i * 0.03,
                        ease: "easeInOut",
                      }}
                    />
                  );
                })}
              </div>

              {/* Playback controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={playHeroVoice}
                    disabled={heroLoading}
                    className="w-11 h-11 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center text-white shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/40 transition-all disabled:opacity-50"
                  >
                    {heroLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : heroPlaying ? (
                      <Pause size={16} className="fill-current" />
                    ) : (
                      <Play size={16} className="fill-current ml-0.5" />
                    )}
                  </button>
                  <div className="text-xs text-neutral-500 dark:text-white/80 font-mono">
                    {heroPlaying ? <span className="text-cyan-600 dark:text-cyan-300 font-semibold animate-pulse">Playing...</span> : T("hero.studio_visual_title")}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-32 bg-neutral-100 dark:bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                    />
                  </div>
                </div>
              </div>

              {/* Voice settings preview */}
              <div className="grid grid-cols-4 gap-2 pt-2">
                {[
                  { label: "STABILITY", value: "75%" },
                  { label: "CLARITY", value: "85%" },
                  { label: "SPEED", value: "1.0x" },
                  { label: "STYLE", value: "Natural" },
                ].map((s) => (
                  <div key={s.label} className="bg-neutral-50 dark:bg-slate-900/50 rounded-xl p-3 text-center">
                    <div className="text-[10px] text-neutral-400 dark:text-slate-500 font-medium tracking-wider">
                      {s.label}
                    </div>
                    <div className="text-sm font-bold text-neutral-800 dark:text-slate-100 mt-1">{s.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Floating badge - top right */}
            <motion.div
              initial={{ opacity: 0, x: 30, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ delay: 0.8, type: "spring", stiffness: 200, damping: 15 }}
              className="absolute -right-3 top-6 bg-white dark:bg-slate-900 rounded-xl border border-neutral-200 dark:border-white/10 shadow-xl p-3 flex items-center gap-2"
            >
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="flex items-center gap-2"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center text-green-600 text-xs font-bold">
                  AI
                </div>
                <div>
                  <div className="text-xs font-semibold text-neutral-800 dark:text-slate-100">Creaneo Orion</div>
                  <div className="text-[10px] text-neutral-500 dark:text-white/80">Latest model</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Floating badge - bottom left */}
            <motion.div
              initial={{ opacity: 0, x: -30, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ delay: 1.0, type: "spring", stiffness: 200, damping: 15 }}
              className="absolute -left-3 bottom-8 bg-white dark:bg-slate-900 rounded-xl border border-neutral-200 dark:border-white/10 shadow-xl p-3 flex items-center gap-2"
            >
              <motion.div
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                className="flex items-center gap-2"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-100 to-teal-100 flex items-center justify-center">
                  <Headphones size={14} className="text-cyan-600 dark:text-cyan-300" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-neutral-800 dark:text-slate-100">2,600+ Voices</div>
                  <div className="text-[10px] text-neutral-500 dark:text-white/80">25+ languages</div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
